import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LayoutDashboard, History, CreditCard, User, TrendingUp, Users, Wrench, Car, Megaphone, Wallet, ChevronDown, ChevronRight, ShoppingCart, PieChart, Settings, Calendar, Target, BarChart3, Activity, LineChart, Flag, BookOpen, MapPin, CalendarCheck, Clock, Shield, Swords, Receipt } from 'lucide-react';
import api from '../utils/api';

const navSections = [
  {
    title: 'Overblik',
    items: [
      { path: '/', label: 'Ledelse', icon: LayoutDashboard },
      { path: '/kpier', label: 'KPIer', icon: Target },
      { path: '/kundeservice', label: 'Kundeservice', icon: Users },
    ]
  },
  {
    title: 'Kørende salg',
    items: [
      { path: '/pipeline', label: 'Booking Pipeline', icon: TrendingUp },
      { path: '/spend-analyse', label: 'Spend Analyse', icon: BarChart3 },
      { path: '/kapacitetsanalyse', label: 'Kapacitetsanalyse', icon: Activity },
      { path: '/prognose', label: 'Prognose', icon: LineChart },
      { path: '/historik', label: 'Historisk Data', icon: History },
      { path: '/konsulenter', label: 'Konsulenter', icon: Users },
      { path: '/konsulenttimer', label: 'Konsulenttimer', icon: Clock },
      { path: '/f1', label: 'F1 Konkurrence', icon: Flag },
      { path: '/montoerer', label: 'Montører', icon: Wrench },
      { path: '/biler', label: 'Biler', icon: Car, disabled: true },
    ]
  },
  {
    title: 'Marketingsanalyse',
    items: [
      { path: '/marketing', label: 'Marketingskanaler', icon: Megaphone },
      { path: '/marketing-analyse', label: 'Analyse', icon: BarChart3 },
      { path: '/omraaderentabilitet', label: 'Områderentabilitet', icon: MapPin },
      { path: '/konkurrenter', label: 'Konkurrenter', icon: Swords },
    ]
  },
  {
    title: 'Online salg',
    items: [
      { path: '/online-pipeline', label: 'Online Pipeline', icon: ShoppingCart, disabled: true },
    ]
  },
  {
    title: 'Økonomi',
    items: [
      { path: '/saldobalance', label: 'Saldobalance', icon: BookOpen },
      { path: '/okonomisk-analyse', label: 'Økonomisk analyse', icon: LineChart },
      { path: '/maaneds-analyse', label: 'Måneds analyse', icon: Calendar },
      { path: '/budget', label: 'Budget - Kørende salg', icon: PieChart },
      { path: '/budget-cockpit', label: 'Budget - Cockpit', icon: PieChart },
      { path: '/udgifts-analyse', label: 'Udgifts analyse', icon: Receipt },
    ]
  },
  {
    title: 'Lønninger',
    items: [
      { path: '/fastloennede', label: 'Kørende salg', icon: Wallet },
    ]
  },
  {
    title: 'Drift',
    items: [
      { path: '/medarbejder', label: 'Medarbejder Aktivitet', icon: User },
      { path: '/ferieregnsskab', label: 'Ferieregnsskab', icon: Calendar },
      { path: '/indstillinger', label: 'Indstillinger', icon: Settings, disabled: true },
    ]
  },
  {
    title: 'System',
    items: [
      { path: '/admin', label: 'Administration', icon: Shield },
    ]
  },
];

export const Navigation = ({ currentPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState([]);
  const [allowedPages, setAllowedPages] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchAccess = async () => {
      try {
        const res = await api.get('/auth/me');
        setAllowedPages(res.data.allowed_pages || []);
      } catch { setAllowedPages([]); }
    };
    fetchAccess();
  }, []);

  const toggleSection = (title) => {
    setExpandedSections(prev => 
      prev.includes(title) 
        ? prev.filter(t => t !== title)
        : [...prev, title]
    );
  };

  return (
    <>
      {/* Menu Button Only */}
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 rounded-lg bg-[#1A1A1A] text-white hover:bg-[#252525] transition-colors"
        data-testid="nav-menu-toggle"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay & Sidebar */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/80"
          onClick={() => setIsOpen(false)}
        >
          {/* Sidebar Panel */}
          <div 
            className="fixed left-0 top-0 h-full w-72 shadow-2xl transform transition-transform duration-300"
            style={{ backgroundColor: '#1A1A1A' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between" style={{ backgroundColor: '#1A1A1A' }}>
              <h2 className="text-lg font-semibold text-white">Navigation</h2>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Nav Items */}
            <nav className="p-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)', backgroundColor: '#1A1A1A' }}>
              {navSections.map((section) => {
                // Filter items by allowed pages
                const filteredItems = allowedPages === null ? section.items : section.items.filter(item => allowedPages.includes(item.path));
                if (filteredItems.length === 0) return null;

                const isExpanded = expandedSections.includes(section.title);
                
                return (
                  <div key={section.title} className="mb-2">
                    {/* Section Header */}
                    <button
                      onClick={() => toggleSection(section.title)}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-white/50 hover:text-white/70 transition-colors uppercase tracking-wider"
                    >
                      <span>{section.title}</span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Section Items */}
                    {isExpanded && (
                      <ul className="space-y-1 mt-1">
                        {filteredItems.map((item) => {
                          const isActive = location.pathname === item.path;
                          const Icon = item.icon;
                          
                          if (item.disabled) {
                            return (
                              <li key={item.path}>
                                <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-white/30 cursor-not-allowed text-sm">
                                  <Icon className="w-4 h-4" />
                                  <span>{item.label}</span>
                                  <span className="ml-auto text-xs bg-white/10 px-1.5 py-0.5 rounded">Snart</span>
                                </div>
                              </li>
                            );
                          }
                          
                          return (
                            <li key={item.path}>
                              <Link
                                to={item.path}
                                onClick={() => setIsOpen(false)}
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                                  isActive
                                    ? 'bg-[#1E6F7A] text-white'
                                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                                }`}
                                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                              >
                                <Icon className="w-4 h-4" />
                                {item.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10" style={{ backgroundColor: '#1A1A1A' }}>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
